/**
 * The Object Editor's 3D view: a small standalone three.js scene (+z up,
 * orbit camera) showing the object's plan glyph and footprint box on the
 * floor and its 3D model, placed with the app's default transform gizmo.
 * Test mode shows the model at the test size, scaled like a placed object.
 */

import { ArrowPathIcon, CheckIcon, CubeTransparentIcon, TrashIcon, ArrowsPointingInIcon, MapPinIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformGizmo } from '../../../potree/personas/tools/modules/transformGizmo/TransformGizmo';
import type { ObjectDefinitionSpec, ObjectModel3D } from 'shared-types';
import { evaluateCustomGlyph, type CustomParamValues } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { cloneCustomModel } from '../../../potree/sketchObjects/custom/modelCache';
import { fitModelPlacement, modelWorldMatrix, moveModelOrigin, placedModelBounds } from '../../../potree/sketchObjects/custom/modelPlacement';
import { T } from '../../../translation/src';
import { ObjectDefinitionAssetKind } from '../../../types/graphqlTypes';
import { EarthLoader } from '../../../components/loaders/EarthLoader';
import { Button } from '../../../components/Button';
import { testSize, useEditorState, useObjectEditor } from '../EditorContext';
import { PanelButton } from '../panels/fields';
import { MODEL_ACCEPT } from './loadModelFile';
import { ModelOriginBinding } from './ModelOriginBinding';
import { ModelPlacementBinding } from './ModelPlacementBinding';
import { useModelImport } from './useModelImport';

interface ViewThree {
  renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera;
  content: THREE.Group;
  modelHolder: THREE.Group;
  gizmo: TransformGizmo;
  binding: ModelPlacementBinding;
  /** "Edit origin": the gizmo moves the model's origin, not the model. */
  originBinding: ModelOriginBinding;
  requestRender: () => void;
}

const originFrame = (width: number, depth: number) => ({ center: { x: 0, y: 0 }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width, depth, z: 0 });
const isDark = (): boolean => document.documentElement.classList.contains('dark');

function disposeTree(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (child instanceof THREE.LineSegments || child instanceof THREE.Mesh) {
      child.geometry.dispose();
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((m: THREE.Material) => m.dispose());
    }
  });
}

/** Rebuild the view's content for the current document (three.js objects live outside React's state rules). */
function updateScene(
  three: ViewThree,
  {
    spec,
    width,
    depth,
    height,
    params,
    model,
    gizmo,
    editOrigin,
  }: { spec: ObjectDefinitionSpec; width: number; depth: number; height: number; params: CustomParamValues; model: { placement: ObjectModel3D; object: THREE.Object3D } | null; gizmo: boolean; editOrigin: boolean },
): void {
  disposeTree(three.content);
  three.content.clear();
  const segments = evaluateCustomGlyph(spec, originFrame(width, depth), params);
  const glyph = new THREE.BufferGeometry();
  // Just above the floor so the grid doesn't cover it.
  glyph.setAttribute('position', new THREE.Float32BufferAttribute(segments.map((v, i) => (i % 3 === 2 ? 0.002 : v)), 3));
  three.content.add(new THREE.LineSegments(glyph, new THREE.LineBasicMaterial({ color: isDark() ? 0xffffff : 0x1f2937 })));
  const box = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(width, depth, height)), new THREE.LineBasicMaterial({ color: 0x9ca3af, transparent: true, opacity: 0.6 }));
  box.position.z = height / 2;
  three.content.add(box);

  three.modelHolder.clear();
  if (model) {
    three.modelHolder.add(model.object);
    three.modelHolder.matrix.copy(modelWorldMatrix(model.placement, spec.defaultSize, originFrame(width, depth), height, 0));
    three.modelHolder.matrixWorldNeedsUpdate = true;
  }
  three.gizmo.setBinding(model && gizmo ? (editOrigin ? three.originBinding : three.binding) : null);
  three.requestRender();
}

const ModelView_: React.FC = () => {
  const { store, ui, setUi, definition } = useObjectEditor();
  const { spec: draftSpec } = useEditorState();
  const spec = ui.preview?.spec ?? draftSpec;
  const [three, setThree] = useState<ViewThree | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { importModel, removeModel, busy, error, heavyTriangles, dismissError } = useModelImport();
  const [loaded, setLoaded] = useState<{ assetId: string; object: THREE.Object3D } | null>(null);
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  /** Blender's "affect only origins": the gizmo moves the model's origin while the model stays put. */
  const [editOrigin, setEditOrigin] = useState(false);

  const model = spec.model3d ?? null;
  const modelUrl = useMemo(() => (model ? definition.assets.find((a) => a.id === model.assetId && a.kind === ObjectDefinitionAssetKind.ModelGlb)?.url : undefined), [definition.assets, model]);
  const testMode = ui.mode === 'test';
  /**
   * The model is being placed: the edit tab, or a smart object's "edit model" (it has no edit tab). The test
   * placement then makes way for the default size, like the edit tab.
   */
  const placing = !ui.preview && (!testMode || ui.editModel);
  const showTest = testMode && !ui.editModel;
  const setEditModel = (on: boolean) => {
    setUi((u) => ({ ...u, editModel: on }));
    if (!on) setEditOrigin(false);
  };

  // ─── Scene setup: a callback ref builds the scene when the container mounts ──
  const cleanupRef = useRef<(() => void) | null>(null);
  const containerRef = useCallback(
    (container: HTMLDivElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!container) {
        setThree(null);
        return;
      }
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.autoClear = false;
    renderer.domElement.style.display = 'block';
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 500);
    camera.up.set(0, 0, 1);
    camera.position.set(2.2, -2.8, 2);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 0.35);
    controls.update();

    scene.add(new THREE.HemisphereLight(0xffffff, 0x8a8f96, 1.4));
    const sun = new THREE.DirectionalLight(0xffffff, 1.6);
    sun.position.set(3, -4, 6);
    scene.add(sun);
    const grid = new THREE.GridHelper(10, 100, 0x9ca3af, 0xd1d5db);
    grid.rotation.x = Math.PI / 2;
    (Array.isArray(grid.material) ? grid.material : [grid.material]).forEach((m) => {
      m.transparent = true;
      m.opacity = 0.5;
    });
    scene.add(grid);
    const content = new THREE.Group();
    const modelHolder = new THREE.Group();
    modelHolder.matrixAutoUpdate = false;
    scene.add(content, modelHolder);

    const gizmo = new TransformGizmo({ getCamera: () => camera, getDomElement: () => renderer.domElement });
    const binding = new ModelPlacementBinding(store);
    const originBinding = new ModelOriginBinding(store);

    let frame = 0;
    const render = () => {
      frame = 0;
      renderer.setClearColor(isDark() ? 0x262626 : 0xf4f5f5);
      renderer.clear();
      renderer.render(scene, camera);
      if (gizmo.getBinding()) {
        gizmo.update(camera, renderer.domElement);
        renderer.clearDepth();
        renderer.render(gizmo.scene, camera);
      }
    };
    const requestRender = () => {
      if (!frame) frame = requestAnimationFrame(render);
    };
    controls.addEventListener('change', requestRender);

    const resize = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width === 0 || height === 0) return;
      renderer.setSize(width, height, false);
      renderer.domElement.style.width = `${width}px`;
      renderer.domElement.style.height = `${height}px`;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      requestRender();
    });
    resize.observe(container);

    // Gizmo drags win over orbiting: listen in the capture phase on the container.
    let dragging = false;
    const pixel = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      return new THREE.Vector2(e.clientX - rect.left, e.clientY - rect.top);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !gizmo.getBinding()) return;
      const px = pixel(e);
      const handle = gizmo.updateActiveHandle(px);
      if (!handle) return;
      const worldPoint = gizmo.getHandleIntersection(handle, px);
      if (worldPoint && gizmo.startDrag(handle, px, worldPoint)) {
        dragging = true;
        controls.enabled = false;
        container.setPointerCapture(e.pointerId);
        e.stopPropagation();
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!gizmo.getBinding()) return;
      // Shift or Ctrl/Cmd: free move, no snapping.
      if (dragging) gizmo.processDrag(pixel(e), { override: e.shiftKey || e.ctrlKey || e.metaKey });
      else gizmo.updateActiveHandle(pixel(e));
      requestRender();
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      controls.enabled = true;
      const active = gizmo.getBinding() === originBinding ? originBinding : binding;
      if (gizmo.endDrag()) active.commitDrag();
      else active.cancelDrag();
      requestRender();
    };
    container.addEventListener('pointerdown', onDown, true);
    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerup', onUp);

    setThree({ renderer, camera, content, modelHolder, gizmo, binding, originBinding, requestRender });
    cleanupRef.current = () => {
      container.removeEventListener('pointerdown', onDown, true);
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerup', onUp);
      resize.disconnect();
      if (frame) cancelAnimationFrame(frame);
      controls.dispose();
      gizmo.dispose();
      disposeTree(content);
      renderer.dispose();
      renderer.domElement.remove();
    };
    },
    [store],
  );

  // ─── The model file (cached per asset) ─────────────────────────────
  // Only a different asset needs a new load; placement edits don't.
  const assetId = model?.assetId;
  useEffect(() => {
    if (!assetId || !modelUrl) return;
    let cancelled = false;
    cloneCustomModel(assetId, modelUrl)
      .then((object) => {
        if (cancelled) return;
        setLoadFailed(null);
        setLoaded({ assetId, object });
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadFailed(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [assetId, modelUrl]);

  /** The model file's own bounds (a parentless copy, so no placement applies): fitting and origin snaps measure it. */
  const meshBounds = useMemo(() => {
    if (!loaded) return null;
    const copy = loaded.object.clone(true);
    copy.position.set(0, 0, 0);
    copy.updateMatrixWorld(true);
    return new THREE.Box3().setFromObject(copy);
  }, [loaded]);
  useEffect(() => three?.originBinding.setMeshBounds(meshBounds), [three, meshBounds]);

  // ─── Scene content: glyph, footprint box, placed model, gizmo ──────
  useEffect(() => {
    if (!three) return;
    const { width, depth, height } = showTest ? testSize(ui.test, spec) : spec.defaultSize;
    const shownModel = model && loaded && loaded.assetId === model.assetId ? { placement: model, object: loaded.object } : null;
    updateScene(three, { spec, width, depth, height, params: showTest ? ui.test.params : {}, model: shownModel, gizmo: placing, editOrigin });
  }, [three, spec, showTest, placing, ui.test, model, loaded, editOrigin]);

  const refit = () => {
    if (!meshBounds || !model) return;
    // A fresh fit, keeping its rotation and its origin on the model.
    const placement = fitModelPlacement(meshBounds, model.rotation, spec.defaultSize, model.pivot);
    store.apply((s) => (s.model3d ? { ...s, model3d: { ...s.model3d, ...placement } } : s));
  };

  /** Put the origin on a point of the model's box (the model doesn't move): its center, or its bottom center. */
  const originTo = (where: 'center' | 'bottom') => {
    if (!meshBounds || !model) return;
    const box = placedModelBounds(model, meshBounds);
    const target = box.getCenter(new THREE.Vector3());
    if (where === 'bottom') target.z = box.min.z;
    store.apply((s) => (s.model3d ? { ...s, model3d: moveModelOrigin(s.model3d, target) } : s));
  };

  const openFile = () => fileInput.current?.click();

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div ref={containerRef} className="h-full w-full" />
      <input
        ref={fileInput}
        type="file"
        accept={MODEL_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void importModel(file);
        }}
      />

      {!model && !busy && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex max-w-sm flex-col items-center gap-3 rounded-2xl bg-white/90 p-6 text-center shadow-md dark:bg-eerie-black/90">
            <CubeTransparentIcon className="h-10 w-10 text-gray-400" />
            <p className="font-medium">
              <T _str="add a 3D model" swc />
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <T _str="glTF/GLB, OBJ, FBX, STL, 3MF, PLY, STEP, IGES or IFC; it is placed on the footprint and stretches with the object" swc />
            </p>
            <Button variant="primary" size="xs" onClick={openFile}>
              <T _str="choose a file" swc />
            </Button>
          </div>
        </div>
      )}
      {busy && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/60 dark:bg-black/40">
          <EarthLoader />
          <T _str="converting the model…" swc />
        </div>
      )}

      {model && (
        <div className="absolute left-3 top-3 flex gap-1 rounded-lg bg-white/90 p-1 shadow-sm dark:bg-eerie-black/90">
          <PanelButton onClick={openFile}>
            <ArrowPathIcon className="h-3.5 w-3.5" />
            <T _str="replace" swc />
          </PanelButton>
          <PanelButton onClick={refit} disabled={!loaded}>
            <ArrowsPointingInIcon className="h-3.5 w-3.5" />
            <T _str="fit to object" swc />
          </PanelButton>
          {/* A smart object has no edit tab: its pencil turns the edit tab's model controls on and off. */}
          {definition.smart && testMode && !ui.preview && (
            <span className={classNames('rounded-md', ui.editModel && 'bg-neon-green-300 text-black')}>
              <PanelButton onClick={() => setEditModel(!ui.editModel)} disabled={!loaded}>
                {ui.editModel ? <CheckIcon className="h-3.5 w-3.5" /> : <PencilSquareIcon className="h-3.5 w-3.5" />}
                {ui.editModel ? <T _str="done" swc /> : <T _str="edit model" swc />}
              </PanelButton>
            </span>
          )}
          {placing && (
            <span className={classNames('rounded-md', editOrigin && 'bg-neon-green-300 text-black')}>
              <PanelButton onClick={() => setEditOrigin((on) => !on)} disabled={!loaded}>
                <MapPinIcon className="h-3.5 w-3.5" />
                <T _str="edit origin" swc />
              </PanelButton>
            </span>
          )}
          {editOrigin && placing && (
            <>
              <PanelButton onClick={() => originTo('center')} disabled={!loaded}>
                <T _str="origin to center" swc />
              </PanelButton>
              <PanelButton onClick={() => originTo('bottom')} disabled={!loaded}>
                <T _str="origin to bottom" swc />
              </PanelButton>
            </>
          )}
          <PanelButton
            danger
            onClick={() => {
              setEditModel(false);
              removeModel();
            }}
          >
            <TrashIcon className="h-3.5 w-3.5" />
            <T _str="remove" swc />
          </PanelButton>
        </div>
      )}

      {/* Clear of the editor's shortcuts button in the bottom-right corner. */}
      <div className="pointer-events-none absolute bottom-4 left-4 right-16 flex flex-col items-end gap-2">
        {(error || loadFailed) && (
          <p role="alert" className="pointer-events-auto rounded-lg bg-error-red-500/10 px-3 py-2 text-sm text-error-red-500">
            <T _str="the model could not be loaded" swc />
            {error ? ` (${error})` : loadFailed ? ` (${loadFailed})` : ''}{' '}
            {error && (
              <button type="button" className="underline" onClick={dismissError}>
                <T _str="dismiss" />
              </button>
            )}
          </p>
        )}
        {model && !modelUrl && (
          <p className="rounded-lg bg-warning-yellow-200/90 px-3 py-2 text-sm text-gray-900">
            <T _str="the model file is missing: replace it" swc />
          </p>
        )}
        {heavyTriangles && (
          <p className="rounded-lg bg-warning-yellow-200/90 px-3 py-2 text-sm text-gray-900">
            <T _str="this model has {count} triangles and may slow projects down; a simplified version works better" args={{ count: heavyTriangles.toLocaleString() }} swc />
          </p>
        )}
        {model && placing && (
          <p className="rounded bg-white/80 px-2 py-1 text-xs text-gray-600 dark:bg-black/60 dark:text-gray-300">
            {editOrigin ? (
              <T _str="drag the arrows to move the model's origin (it snaps to the model's box; Shift: free); the model stays where it is and turns and scales around it" swc />
            ) : testMode ? (
              <T _str="drag the arrows, rings and cubes to place the model; click done to see it resize with the object" swc />
            ) : (
              <T _str="drag the arrows, rings and cubes to place the model; switch to Test to see it resize with the object" swc />
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export const ModelView = memo(ModelView_);
